import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from 'lucide-react';
interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const days = Array.from({
  length: 31
}, (_, i) => i + 1);
const times = ['9:00 AM', '10:30 AM', '1:00 PM', '2:30 PM', '4:00 PM'];
export function DemoModal({
  isOpen,
  onClose
}: DemoModalProps) {
  const [step, setStep] = useState<'form' | 'calendar' | 'success'>('form');
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('calendar');
  };
  const handleConfirmBooking = () => {
    setStep('success');
    setTimeout(() => {
      setStep('form');
      setSelectedDate(null);
      setSelectedTime(null);
      onClose();
    }, 3000);
  };
  return <AnimatePresence data-id="element-31" data-mp="3uf7zv1ksgtdj">
      {isOpen && <div role="dialog" aria-modal="true" aria-label="Schedule a consultation" className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-32" data-mp="3ueg2pmbjmnl5">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-midnight-navy/70 backdrop-blur-md" data-id="element-33" data-mp="3kicymax4sevk" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 20
      }} className="relative w-full max-w-lg bg-stark-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" data-id="element-34" data-mp="lx22kcwfrdl0">
            <button onClick={onClose} aria-label="Close dialog" className="absolute top-4 right-4 z-10 p-2 text-midnight-navy/40 hover:text-midnight-navy transition-colors" data-id="element-35" data-mp="13atg1ce9t9ue">
              <XIcon className="w-6 h-6" data-id="element-36" data-mp="ok41q15ijemb" />
            </button>

            <div className="p-8 md:p-12 overflow-y-auto custom-scrollbar" data-id="element-37" data-mp="ohqbc5ls2ql3">
              <AnimatePresence mode="wait" data-id="element-38" data-mp="jlj5sdxanh3o">
                {step === 'form' && <motion.div key="form" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-8" data-id="element-39" data-mp="fi36f9tub2na">
                    <div className="space-y-2" data-id="element-40" data-mp="27iscrbh6atct">
                      <h2 className="text-3xl font-montserrat font-extrabold text-midnight-navy" data-id="element-41" data-mp="27hz8ch5osfxu">
                        Schedule a Consultation
                      </h2>
                      <p className="text-midnight-navy/60" data-id="element-42" data-mp="2h9d4ceyfixm8">
                        Tell us about your business and we'll bring the right
                        specialist to the call.
                      </p>
                    </div>

                    <form onSubmit={handleFormSubmit} className="space-y-4" data-id="element-43" data-mp="1olr33dcoc52j">
                      <div className="grid grid-cols-2 gap-4" data-id="element-44" data-mp="1tj9edae5ywdp">
                        <div className="space-y-1.5" data-id="element-45" data-mp="1yhavhps14lmj">
                          <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40" data-id="element-46" data-mp="1yjx3trmqv55t">
                            First Name
                          </label>
                          <input required type="text" className="w-full px-4 py-3 bg-cool-gray border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors" placeholder="Jane" data-id="element-47" data-mp="39whcgsstjs28" />
                        </div>
                        <div className="space-y-1.5" data-id="element-48" data-mp="3mgec35hqc3kg">
                          <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40" data-id="element-49" data-mp="3hgs0f0hjuvdr">
                            Last Name
                          </label>
                          <input required type="text" className="w-full px-4 py-3 bg-cool-gray border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors" placeholder="Doe" data-id="element-50" data-mp="3vdx44ttsji5u" />
                        </div>
                      </div>

                      <div className="space-y-1.5" data-id="element-51" data-mp="dzrvu9mux2ur">
                        <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40" data-id="element-52" data-mp="e1ct470k2zwb">
                          Email
                        </label>
                        <input required type="email" className="w-full px-4 py-3 bg-cool-gray border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors" placeholder="jane@example.com" data-id="element-53" data-mp="11nghj3vbtfl3" />
                      </div>

                      <div className="space-y-1.5" data-id="element-54" data-mp="ioufnmrzjkg4">
                        <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40" data-id="element-55" data-mp="ipm2a83lu7u9">
                          Phone
                        </label>
                        <input required type="tel" className="w-full px-4 py-3 bg-cool-gray border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors" placeholder="(555) 000-0000" data-id="element-56" data-mp="9wsgdrtnka7f" />
                      </div>

                      <div className="space-y-1.5" data-id="element-57" data-mp="387lx5r1an640">
                        <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40" data-id="element-58" data-mp="38614k6yjhx8s">
                          Message
                        </label>
                        <textarea rows={3} className="w-full px-4 py-3 bg-cool-gray border border-midnight-navy/10 rounded-lg focus:outline-none focus:border-royal-blue transition-colors resize-none" placeholder="How can we help you?" data-id="element-59" data-mp="2yb98x8oipkgn" />
                      </div>

                      <button type="submit" className="w-full bg-royal-blue text-stark-white py-4 rounded-full font-bold hover:bg-royal-blue-dark transition-all shadow-xl shadow-royal-blue/20 flex items-center justify-center gap-2 mt-4" data-id="element-60" data-mp="3h0yz1ihc5i28">
                        Schedule Walkthrough
                        <CalendarIcon className="w-4 h-4" data-id="element-61" data-mp="l158dlb892w6" />
                      </button>
                    </form>
                  </motion.div>}

                {step === 'calendar' && <motion.div key="calendar" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 20
            }} className="space-y-8" data-id="element-62" data-mp="zrw0kugqe3zt">
                    <div className="flex items-center gap-4" data-id="element-63" data-mp="1ipythkcegl2w">
                      <button onClick={() => setStep('form')} aria-label="Back to details" className="p-2 hover:bg-cool-gray rounded-full transition-colors" data-id="element-64" data-mp="3fk2167kqld0k">
                        <ChevronLeftIcon className="w-5 h-5 text-midnight-navy" data-id="element-65" data-mp="1q7wrosp4s9qt" />
                      </button>
                      <div className="space-y-1" data-id="element-66" data-mp="2002pij22dldx">
                        <h2 className="text-2xl font-montserrat font-extrabold text-midnight-navy" data-id="element-67" data-mp="1zzj9j7vz572j">
                          Select Date &amp; Time
                        </h2>
                        <p className="text-sm text-midnight-navy/60" data-id="element-68" data-mp="17alureqz1sej">
                          Choose a slot for your consultation.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6" data-id="element-69" data-mp="1hacld5cevn9c">
                      <div className="bg-cool-gray p-4 rounded-2xl border border-midnight-navy/10" data-id="element-70" data-mp="1cahxlanxndsi">
                        <div className="flex items-center justify-between mb-4 px-2" data-id="element-71" data-mp="2soq641bvbml5">
                          <span className="font-bold text-midnight-navy" data-id="element-72" data-mp="2sqkuxqq9fyui">
                            July 2026
                          </span>
                          <div className="flex gap-2" data-id="element-73" data-mp="2v84prucc51qq">
                            <ChevronLeftIcon className="w-4 h-4 text-midnight-navy/30" data-id="element-74" data-mp="2vbis52l01ay6" />
                            <ChevronRightIcon className="w-4 h-4 text-midnight-navy" data-id="element-75" data-mp="30668ge5umz1o" />
                          </div>
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-76" data-mp="2q9bco30yncr3">
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span key={`${d}-${i}`} className="text-[10px] font-bold text-midnight-navy/30" data-id="element-77" data-mp="3iyqy7mq94o19" data-mpi={i}>
                              {d}
                            </span>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1" data-id="element-78" data-mp="slzqhfej2ub">
                          {days.map(d => <button key={d} onClick={() => setSelectedDate(d)} className={`aspect-square text-sm rounded-lg flex items-center justify-center transition-all ${selectedDate === d ? 'bg-royal-blue text-stark-white font-bold' : 'hover:bg-stark-white text-midnight-navy'}`} data-id="element-79" data-mp="5k45rjwa7tm5">
                              {d}
                            </button>)}
                        </div>
                      </div>

                      <div className="space-y-3" data-id="element-80" data-mp="1pkj9v9bkgfhy">
                        <span className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40" data-id="element-81" data-mp="1plb86rwjsrbx">
                          Available Times
                        </span>
                        <div className="grid grid-cols-2 gap-2" data-id="element-82" data-mp="1ftx2u3hx0xav">
                          {times.map(t => <button key={t} onClick={() => setSelectedTime(t)} className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${selectedTime === t ? 'bg-midnight-navy text-stark-white border-midnight-navy' : 'border-midnight-navy/10 text-midnight-navy hover:border-royal-blue'}`} data-id="element-83" data-mp="11w8fmp0dle2n">
                              {t}
                            </button>)}
                        </div>
                      </div>

                      <button disabled={!selectedDate || !selectedTime} onClick={handleConfirmBooking} className="w-full bg-royal-blue text-stark-white py-4 rounded-full font-bold hover:bg-royal-blue-dark transition-all shadow-xl shadow-royal-blue/20 disabled:opacity-40 disabled:cursor-not-allowed mt-4" data-id="element-84" data-mp="166ax2py713x5">
                        Confirm Booking
                      </button>
                    </div>
                  </motion.div>}

                {step === 'success' && <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="py-12 text-center space-y-6" data-id="element-85" data-mp="hlmhr3b81ox4">
                    <div className="w-20 h-20 bg-royal-blue/10 text-royal-blue rounded-full flex items-center justify-center mx-auto" data-id="element-86" data-mp="10bugzabsvv81">
                      <CheckIcon className="w-10 h-10" data-id="element-87" data-mp="159ci4ejkt65h" />
                    </div>
                    <div className="space-y-2" data-id="element-88" data-mp="19c0totzocr65">
                      <h3 className="text-2xl font-montserrat font-extrabold text-midnight-navy" data-id="element-89" data-mp="19a6432dgkavq">
                        Thank you for your submission
                      </h3>
                      <p className="text-midnight-navy/60" data-id="element-90" data-mp="3b56t5zyzdstu">
                        Your consultation is scheduled for July {selectedDate} at{' '}
                        {selectedTime}.
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}